import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mail, Phone, MessageSquare, User, Calendar, CheckCircle, Clock, XCircle,
  Bell, Settings, Plus, Trash2, Save, Send, Loader2, X, ExternalLink, Building2} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInquiries() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNotifSettings, setShowNotifSettings] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      // Inquiries: all roles can access
      const inquiriesRes = await axios.get(`${API}/inquiries`);
      setInquiries(inquiriesRes.data);

      // Users (for broker assignment): only admin/manager have access
      try {
        const usersRes = await axios.get(`${API}/users`);
        setUsers((usersRes.data || []).filter(u => u.role === 'broker' || u.role === 'manager'));
      } catch {
        setUsers([]); // No assignment feature for this role - silently ignore
      }
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 401) {
        toast.error('Сесията е изтекла — моля влезте отново');
      } else if (status === 403) {
        toast.error('Нямате права за достъп до запитванията');
      } else {
        toast.error(`Грешка при зареждане на запитванията${detail ? ': ' + detail : ''}`);
      }
      console.error('Inquiries load error:', status, detail, error);
    } finally { setLoading(false); }
  };

  const updateStatus = async (inquiryId, status) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}/status?status=${status}`);
      toast.success('Статусът е обновен');
      fetchData();
    } catch { toast.error('Грешка при обновяване на статуса'); }
  };

  const deleteInquiry = async (inquiryId, name) => {
    if (!window.confirm(`Изтриване на запитването от "${name}"? Това действие е необратимо.`)) return;
    try {
      await axios.delete(`${API}/inquiries/${inquiryId}`);
      toast.success('Запитването е изтрито');
      fetchData();
    } catch { toast.error('Грешка при изтриване'); }
  };

  const assignBroker = async (inquiryId, brokerId) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}/assign?broker_id=${brokerId}`);
      toast.success('Брокерът е назначен');
      fetchData();
    } catch { toast.error('Грешка при назначаване на брокер'); }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <Clock className="w-4 h-4" />;
      case 'assigned': return <User className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return t('inquiries.status.new');
      case 'assigned': return 'Назначено';
      case 'completed': return 'Завършено';
      case 'cancelled': return 'Отказано';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredInquiries = inquiries
    .filter(inquiry => statusFilter === 'all' || inquiry.status === statusFilter)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-inquiries">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">{t('inquiries.title')}</h1>
          <p className="text-muted-foreground">{t('inquiries.page_subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <Button 
              variant="outline" 
              onClick={() => setShowNotifSettings(true)}
              data-testid="notification-settings-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Имейл нотификации
            </Button>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="inquiry-status-filter">
              <SelectValue placeholder="Филтър по статус" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('inquiries.all')}</SelectItem>
              <SelectItem value="new">{t('inquiries.new')}</SelectItem>
              <SelectItem value="assigned">Назначени</SelectItem>
              <SelectItem value="completed">Завършени</SelectItem>
              <SelectItem value="cancelled">Отказани</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Inquiries List */}
      {filteredInquiries.length === 0 ? (
        <div className="admin-card text-center py-12">
          <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {statusFilter === 'all' ? 'Няма запитвания' : 'Няма запитвания с този статус'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInquiries.map((inquiry) => (
            <div key={inquiry.id} className="admin-card">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <Badge className={`flex items-center gap-1 ${getStatusColor(inquiry.status)}`}>
                      {getStatusIcon(inquiry.status)}
                      {getStatusLabel(inquiry.status)}
                    </Badge>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(inquiry.created_at).toLocaleDateString('bg-BG', {
                        day: 'numeric', month: 'long', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                    {inquiry.property_id && (
                      <a
                        href={`/properties/${inquiry.property_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm flex items-center gap-1 text-[#1B5E37] hover:underline"
                        title="Отвори проекта"
                      >
                        <Building2 className="w-4 h-4" />
                        {inquiry.property_name || 'Проект'}
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                    )}
                    {inquiry.listing_id && (
                      <a
                        href={`/listings/${inquiry.listing_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
                        title="Отвори обявата"
                      >
                        <Building2 className="w-4 h-4" />
                        {inquiry.listing_title || 'Обява'}
                        <ExternalLink className="w-3 h-3 opacity-50" />
                      </a>
                    )}
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => deleteInquiry(inquiry.id, inquiry.name)}
                        className="ml-auto p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Изтрий запитването"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      {(user?.role === 'admin' || user?.role === 'manager') ? (
                        <button
                          onClick={() => {
                            // Find contact by email and navigate to profile
                            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`)
                              .then(r => {
                                const contact = (r.data || []).find(c => c.email === inquiry.email);
                                if (contact) navigate(`/admin/contacts/${contact.id}`);
                                else toast.info('Контактът не е намерен в CRM');
                              });
                          }}
                          className="font-medium text-[#1B5E37] hover:underline cursor-pointer flex items-center gap-1"
                          title="Отвори CRM профил"
                        >
                          {inquiry.name}
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </button>
                      ) : (
                        <span className="font-medium">{inquiry.name}</span>
                      )}
                    </div>
                    <div>
                      <a href={`mailto:${inquiry.email}`} className="flex items-center gap-2 text-primary hover:underline">
                        <Mail className="w-4 h-4" />{inquiry.email}
                      </a>
                    </div>
                    <div>
                      <a href={`tel:${inquiry.phone}`} className="flex items-center gap-2 text-primary hover:underline">
                        <Phone className="w-4 h-4" />{inquiry.phone}
                      </a>
                    </div>
                  </div>
                  <div className="p-4 bg-secondary rounded-lg">
                    <div className="flex items-start gap-2">
                      <MessageSquare className="w-4 h-4 text-muted-foreground mt-1" />
                      <p className="text-sm">{inquiry.message}</p>
                    </div>
                  </div>
                </div>

                <div className="lg:w-64 flex flex-col gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Статус</label>
                    <Select value={inquiry.status} onValueChange={(value) => updateStatus(inquiry.id, value)}>
                      <SelectTrigger data-testid={`inquiry-status-${inquiry.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Ново</SelectItem>
                        <SelectItem value="assigned">Назначено</SelectItem>
                        <SelectItem value="completed">Завършено</SelectItem>
                        <SelectItem value="cancelled">Отказано</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Назначен брокер</label>
                    <Select value={inquiry.assigned_broker_id || 'none'} onValueChange={(value) => value !== 'none' && assignBroker(inquiry.id, value)}>
                      <SelectTrigger data-testid={`inquiry-broker-${inquiry.id}`}><SelectValue placeholder="Избери брокер" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Не е назначен</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-sm text-muted-foreground mt-6">
        Показани {filteredInquiries.length} от {inquiries.length} запитвания
      </p>

      {/* Notification Settings Modal */}
      <NotificationSettingsModal 
        open={showNotifSettings} 
        onClose={() => setShowNotifSettings(false)} 
      />
    </div>
  );
}


function NotificationSettingsModal({ open, onClose }) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true,
    sender_email: '',
    sender_name: 'GreenCastle',
    recipients: [],
    enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '', role: '' });

  useEffect(() => {
    if (open) {
      setLoading(true);
      setTestResult(null);
      axios.get(`${API}/notification-settings`).then(r => {
        const s = r.data;
        setSettings({
          smtp_host: s.smtp_host || '',
          smtp_port: s.smtp_port || 587,
          smtp_user: s.smtp_user || '',
          smtp_password: s.smtp_password_masked || s.smtp_password || '',
          smtp_use_tls: s.smtp_use_tls !== false,
          sender_email: s.sender_email || '',
          sender_name: s.sender_name || 'GreenCastle',
          recipients: s.recipients || [],
          enabled: s.enabled || false
        });
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-settings`, settings);
      toast.success('Настройките за нотификации са запазени');
    } catch { toast.error('Грешка при запазване'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Въведете имейл за тест'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      // Save first so backend uses latest settings
      await axios.put(`${API}/notification-settings`, settings);
      const r = await axios.post(`${API}/notification-settings/test`, { test_email: testEmail });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.detail || 'Грешка' });
    } finally { setTesting(false); }
  };

  const addRecipient = () => {
    if (!newRecipient.email) { toast.error('Въведете имейл'); return; }
    if (settings.recipients.some(r => r.email === newRecipient.email)) {
      toast.error('Този имейл вече е добавен');
      return;
    }
    setSettings(s => ({
      ...s,
      recipients: [...s.recipients, { ...newRecipient, active: true, id: Date.now().toString() }]
    }));
    setNewRecipient({ name: '', email: '', role: '' });
  };

  const removeRecipient = (email) => {
    setSettings(s => ({
      ...s,
      recipients: s.recipients.filter(r => r.email !== email)
    }));
  };

  const toggleRecipient = (email) => {
    setSettings(s => ({
      ...s,
      recipients: s.recipients.map(r => r.email === email ? { ...r, active: !r.active } : r)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#1B5E37]" />
            Имейл нотификации при ново запитване
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="spinner"></div></div>
        ) : (
          <div className="space-y-6">
            {/* Enable toggle */}
            <div className="flex items-center justify-between p-4 bg-secondary rounded-lg border border-border">
              <div>
                <Label className="font-medium">{t('inquiries.activate_notifs')}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  При ново запитване изпраща имейл до избраните получатели
                </p>
              </div>
              <button
                onClick={() => setSettings(s => ({ ...s, enabled: !s.enabled }))}
                className={`w-12 h-6 rounded-full transition-colors ${settings.enabled ? 'bg-[#1B5E37]' : 'bg-gray-300'}`}
                data-testid="notif-enabled-toggle"
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Recipients */}
            <div>
              <h3 className="font-heading font-semibold text-base mb-3">{t('inquiries.recipients')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Добавете имейли на хора, които трябва да получават известие при ново запитване.
              </p>

              {/* Add new recipient */}
              <div className="flex gap-2 mb-4">
                <Input 
                  value={newRecipient.name}
                  onChange={(e) => setNewRecipient(r => ({ ...r, name: e.target.value }))}
                  placeholder="Име (напр. Иван)"
                  className="w-32"
                  data-testid="new-recipient-name"
                />
                <Input 
                  value={newRecipient.email}
                  onChange={(e) => setNewRecipient(r => ({ ...r, email: e.target.value }))}
                  placeholder="Имейл адрес"
                  type="email"
                  className="flex-1"
                  data-testid="new-recipient-email"
                />
                <Input 
                  value={newRecipient.role}
                  onChange={(e) => setNewRecipient(r => ({ ...r, role: e.target.value }))}
                  placeholder="Роля (напр. Брокер)"
                  className="w-32"
                  data-testid="new-recipient-role"
                />
                <Button variant="outline" onClick={addRecipient} data-testid="add-recipient-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Recipients list */}
              {settings.recipients.length > 0 ? (
                <div className="space-y-2">
                  {settings.recipients.map((recipient) => (
                    <div 
                      key={recipient.email}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        recipient.active ? 'bg-white border-border' : 'bg-gray-50 border-gray-200 opacity-60'
                      }`}
                      data-testid={`recipient-${recipient.email}`}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleRecipient(recipient.email)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            recipient.active ? 'bg-[#1B5E37] border-[#1B5E37]' : 'border-gray-300 bg-white'
                          }`}
                        >
                          {recipient.active && <CheckCircle className="w-3 h-3 text-white" />}
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{recipient.name || recipient.email}</span>
                            {recipient.role && (
                              <span className="text-xs bg-secondary px-2 py-0.5 rounded text-muted-foreground">
                                {recipient.role}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{recipient.email}</span>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeRecipient(recipient.email)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
                  <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">{t('inquiries.no_recipients')}</p>
                </div>
              )}
            </div>

            {/* SMTP Settings */}
            <div>
              <h3 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                SMTP Настройки
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('inquiries.smtp_server')}</Label>
                  <Input 
                    value={settings.smtp_host}
                    onChange={(e) => setSettings(s => ({ ...s, smtp_host: e.target.value }))}
                    placeholder="smtp.gmail.com"
                    className="mt-1 font-mono text-sm"
                    data-testid="smtp-host-input"
                  />
                </div>
                <div>
                  <Label>{t('inquiries.smtp_port')}</Label>
                  <Input 
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings(s => ({ ...s, smtp_port: parseInt(e.target.value) || 587 }))}
                    placeholder="587"
                    className="mt-1"
                    data-testid="smtp-port-input"
                  />
                </div>
                <div>
                  <Label>{t('admin.email')}</Label>
                  <Input 
                    value={settings.smtp_user}
                    onChange={(e) => setSettings(s => ({ ...s, smtp_user: e.target.value }))}
                    placeholder="office@greencastle.bg"
                    className="mt-1"
                    data-testid="smtp-user-input"
                  />
                </div>
                <div>
                  <Label>{t('inquiries.smtp_password')}</Label>
                  <Input 
                    type="password"
                    value={settings.smtp_password}
                    onChange={(e) => setSettings(s => ({ ...s, smtp_password: e.target.value }))}
                    placeholder="••••••••"
                    className="mt-1 font-mono"
                    data-testid="smtp-password-input"
                  />
                </div>
                <div>
                  <Label>{t('inquiries.smtp_sender')}</Label>
                  <Input 
                    value={settings.sender_email}
                    onChange={(e) => setSettings(s => ({ ...s, sender_email: e.target.value }))}
                    placeholder="noreply@greencastle.bg"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Ако е празно, ще се ползва SMTP потребителя</p>
                </div>
                <div>
                  <Label>Име на изпращач</Label>
                  <Input 
                    value={settings.sender_name}
                    onChange={(e) => setSettings(s => ({ ...s, sender_name: e.target.value }))}
                    placeholder="GreenCastle"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 mt-4 p-3 bg-secondary rounded-lg">
                <button
                  onClick={() => setSettings(s => ({ ...s, smtp_use_tls: !s.smtp_use_tls }))}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    settings.smtp_use_tls ? 'bg-[#1B5E37] border-[#1B5E37]' : 'border-gray-300 bg-white'
                  }`}
                >
                  {settings.smtp_use_tls && <CheckCircle className="w-3 h-3 text-white" />}
                </button>
                <div>
                  <span className="text-sm font-medium">Използвай TLS/SSL</span>
                  <p className="text-xs text-muted-foreground">Препоръчително за сигурна връзка (порт 587 = STARTTLS, порт 465 = SSL)</p>
                </div>
              </div>
            </div>

            {/* Test email */}
            <div className="p-4 border border-border rounded-lg">
              <h4 className="font-medium text-sm mb-3">{t('inquiries.send_test_email')}</h4>
              <div className="flex gap-2">
                <Input 
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  type="email"
                  className="flex-1"
                  data-testid="test-email-input"
                />
                <Button 
                  variant="outline" 
                  onClick={handleTest} 
                  disabled={testing}
                  data-testid="send-test-email-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Тест
                </Button>
              </div>
              {testResult && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="test-result">
                  {testResult.success ? <CheckCircle className="w-4 h-4 inline mr-2" /> : <XCircle className="w-4 h-4 inline mr-2" />}
                  {testResult.message}
                </div>
              )}
            </div>

            {/* SMTP help */}
            <div className="p-4 bg-secondary rounded-lg text-sm text-muted-foreground">
              <h4 className="font-medium text-foreground mb-2">Помощ за SMTP настройки:</h4>
              <ul className="space-y-1.5">
                <li><strong>Gmail:</strong> smtp.gmail.com, порт 587, TLS. Нужен е App Password от Google Account &gt; Security.</li>
                <li><strong>Abv.bg:</strong> smtp.abv.bg, порт 465, SSL.</li>
                <li><strong>Outlook:</strong> smtp.office365.com, порт 587, TLS.</li>
                <li><strong>Фирмен мейл:</strong> Попитайте вашия хостинг доставчик за SMTP данните.</li>
              </ul>
            </div>

            {/* Save */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#1B5E37] hover:bg-[#154a2c]"
                data-testid="save-notif-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Запазване...' : 'Запази настройки'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
